import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Check, Shield, MessageSquare, Users, AlertTriangle } from "lucide-react";

interface HubPricingProps {
  billingPeriod: "monthly" | "annual";
}

export const HubPricingSection = ({ billingPeriod }: HubPricingProps) => {
  const navigate = useNavigate();
  const [activeHub, setActiveHub] = useState("operations");

  const hubs = {
    allinone: {
      name: "Shiftask All-in-One",
      icon: Shield,
      description: "Complete security workforce management solution with all modules included",
      includes: ["All Security Operations", "All Communications", "All HR & Management", "All Compliance & Safety"],
      tiers: [
        {
          name: "Basic",
          monthlyPrice: 38,
          annualPrice: 32,
          color: "from-blue-500 to-blue-600",
          features: [
            "Everything from all Basic tiers:",
            "Real-time clock in & out with GPS",
            "Unlimited missions and assignments",
            "Mobile patrol check-ins",
            "Up to 5 facility sites",
            "Unlimited personal and group chats",
            "Media and file attachments",
            "Basic incident reports",
            "Personnel database & profiles",
            "Time-off management",
            "Certification expiry alerts",
            "Anonymous whistleblower portal",
            "Safety checklist templates",
            "Document repository",
            "Mobile app (iOS & Android)",
            "Email support"
          ]
        },
        {
          name: "Advanced",
          monthlyPrice: 68,
          annualPrice: 58,
          color: "from-purple-500 to-purple-600",
          popular: true,
          features: [
            "Everything in Basic, plus:",
            "Advanced shift scheduling with auto-assign",
            "Sentinel patrol system with QR/NFC",
            "Up to 20 Geofence sites",
            "Live GPS tracking during patrols",
            "Schedule messages for later",
            "Emergency broadcast alerts",
            "Advanced report templates",
            "Report automation",
            "Recruitment & applicant tracking",
            "Performance review system",
            "Training courses & quizzes",
            "Skills matrix management",
            "Encrypted whistleblower communications",
            "Advanced incident investigation",
            "Root cause analysis",
            "Custom compliance workflows"
          ]
        },
        {
          name: "Professional",
          monthlyPrice: 98,
          annualPrice: 84,
          color: "from-amber-500 to-amber-600",
          features: [
            "Everything in Advanced, plus:",
            "Unlimited facility sites & Geofences",
            "Multiple schedules (up to 12)",
            "Patrol route optimization",
            "Custom checkpoint workflows",
            "Auto-translate messages (multi-language)",
            "Advanced analytics with AI insights",
            "Custom KPI dashboards",
            "Integration with BI tools",
            "Advanced performance analytics",
            "360-degree feedback",
            "Succession planning",
            "Custom training paths",
            "Certification management",
            "Multi-level case management",
            "Regulatory compliance templates",
            "Integration with legal systems",
            "API access for all modules"
          ]
        },
        {
          name: "Enterprise",
          monthlyPrice: null,
          annualPrice: null,
          color: "from-slate-700 to-slate-900",
          features: [
            "Everything in Professional, plus:",
            "Unlimited schedules & facilities",
            "Single Sign-On (SSO)",
            "Two-factor authentication (2FA)",
            "Biometric app lock",
            "Unlimited storage",
            "Custom welcome screens",
            "White-label options",
            "Unlimited training modules",
            "Custom onboarding programs",
            "HRIS integrations",
            "Unlimited case management",
            "Legal holds & e-discovery",
            "Custom compliance frameworks",
            "Multi-jurisdiction support",
            "Dedicated account manager",
            "SLA guarantee",
            "Priority support",
            "Custom integrations & workflows"
          ]
        }
      ]
    },
    operations: {
      name: "Security Operations",
      icon: Shield,
      description: "Core security management tools",
      includes: ["Scheduling", "Missions", "Sentinel Patrol", "Facility Management"],
      tiers: [
        {
          name: "Basic",
          monthlyPrice: 14,
          annualPrice: 12,
          color: "from-blue-500 to-blue-600",
          features: [
            "Real-time clock in & out with GPS",
            "Unlimited missions and assignments",
            "Basic scheduling with shift templates",
            "Mobile patrol check-ins",
            "Up to 5 facility sites",
            "Basic incident reports",
            "Mobile app (iOS & Android)",
            "Email support"
          ]
        },
        {
          name: "Advanced",
          monthlyPrice: 24,
          annualPrice: 20,
          color: "from-purple-500 to-purple-600",
          popular: true,
          features: [
            "Everything in Basic, plus:",
            "Advanced shift scheduling with auto-assign",
            "Sentinel patrol system with QR/NFC",
            "Up to 20 Geofence sites",
            "Recurring shift patterns",
            "Schedule templates library",
            "Live GPS tracking during patrols",
            "Shift swap & cover requests",
            "Advanced mission planning"
          ]
        },
        {
          name: "Professional",
          monthlyPrice: 38,
          annualPrice: 32,
          color: "from-amber-500 to-amber-600",
          features: [
            "Everything in Advanced, plus:",
            "Unlimited facility sites",
            "Unlimited Geofence locations",
            "Multiple schedules (up to 12)",
            "Auto-assign guards based on skills",
            "Patrol route optimization",
            "Custom checkpoint workflows",
            "Integration with access control",
            "Advanced analytics & reporting",
            "API access"
          ]
        },
        {
          name: "Enterprise",
          monthlyPrice: null,
          annualPrice: null,
          color: "from-slate-700 to-slate-900",
          features: [
            "Everything in Professional, plus:",
            "Unlimited schedules & facilities",
            "Single Sign-On (SSO)",
            "Two-factor authentication (2FA)",
            "Biometric app lock",
            "Custom integrations",
            "Dedicated account manager",
            "SLA guarantee",
            "Priority support",
            "Custom workflows"
          ]
        }
      ]
    },
    communications: {
      name: "Communication & Reporting",
      icon: MessageSquare,
      description: "Real-time communication and comprehensive reporting",
      includes: ["Chat & Messaging", "Reports & Analytics", "Updates Feed", "Emergency Alerts"],
      tiers: [
        {
          name: "Basic",
          monthlyPrice: 10,
          annualPrice: 8,
          color: "from-blue-500 to-blue-600",
          features: [
            "Unlimited personal and group chats",
            "Media and file attachments",
            "Basic incident reports",
            "Centralized company feed",
            "Push notifications",
            "Basic analytics dashboard",
            "Shift handover notes"
          ]
        },
        {
          name: "Advanced",
          monthlyPrice: 18,
          annualPrice: 15,
          color: "from-purple-500 to-purple-600",
          popular: true,
          features: [
            "Everything in Basic, plus:",
            "Schedule messages for later",
            "Emergency broadcast alerts",
            "Advanced report templates",
            "Live polls and surveys",
            "Report automation",
            "Custom report builder",
            "Anonymous feedback system",
            "Engagement tracking"
          ]
        },
        {
          name: "Professional",
          monthlyPrice: 28,
          annualPrice: 24,
          color: "from-amber-500 to-amber-600",
          features: [
            "Everything in Advanced, plus:",
            "Auto-translate messages (multi-language)",
            "Custom publishers for updates",
            "Pin important posts",
            "Advanced analytics with AI insights",
            "Custom KPI dashboards",
            "Scheduled report delivery",
            "Integration with BI tools",
            "API access"
          ]
        },
        {
          name: "Enterprise",
          monthlyPrice: null,
          annualPrice: null,
          color: "from-slate-700 to-slate-900",
          features: [
            "Everything in Professional, plus:",
            "Unlimited storage",
            "Custom welcome screens",
            "Advanced security features",
            "Data export & archiving",
            "Compliance reporting",
            "Dedicated support",
            "Custom integrations",
            "White-label options"
          ]
        }
      ]
    },
    hr: {
      name: "HR & Management",
      icon: Users,
      description: "Personnel, performance, and training management",
      includes: ["Personnel Management", "HR & Recruitment", "Performance Reviews", "Training & Certifications"],
      tiers: [
        {
          name: "Basic",
          monthlyPrice: 12,
          annualPrice: 10,
          color: "from-blue-500 to-blue-600",
          features: [
            "Personnel database",
            "Employee profiles & documents",
            "Basic training tracking",
            "Time-off management",
            "Document storage",
            "Certification expiry alerts",
            "Mobile access to profiles"
          ]
        },
        {
          name: "Advanced",
          monthlyPrice: 22,
          annualPrice: 18,
          color: "from-purple-500 to-purple-600",
          popular: true,
          features: [
            "Everything in Basic, plus:",
            "Recruitment & applicant tracking",
            "Candidate pipeline management",
            "Performance review system",
            "Goal setting & tracking",
            "Training courses & quizzes",
            "Skills matrix management",
            "Recognition & rewards program",
            "Custom evaluation forms"
          ]
        },
        {
          name: "Professional",
          monthlyPrice: 34,
          annualPrice: 28,
          color: "from-amber-500 to-amber-600",
          features: [
            "Everything in Advanced, plus:",
            "Advanced performance analytics",
            "360-degree feedback",
            "Succession planning",
            "Custom training paths",
            "Certification management",
            "Compliance training tracking",
            "Custom HR workflows",
            "Integration with payroll",
            "API access"
          ]
        },
        {
          name: "Enterprise",
          monthlyPrice: null,
          annualPrice: null,
          color: "from-slate-700 to-slate-900",
          features: [
            "Everything in Professional, plus:",
            "Unlimited training modules",
            "Custom onboarding programs",
            "Advanced talent analytics",
            "HRIS integrations",
            "Dedicated HR consultant",
            "Custom compliance tracking",
            "Multi-entity management",
            "Advanced security"
          ]
        }
      ]
    },
    compliance: {
      name: "Compliance & Safety",
      icon: AlertTriangle,
      description: "Whistleblowing, incidents, and safety protocols",
      includes: ["Whistleblowers System", "Incident Management", "Safety Protocols", "Compliance Tracking"],
      tiers: [
        {
          name: "Basic",
          monthlyPrice: 8,
          annualPrice: 7,
          color: "from-blue-500 to-blue-600",
          features: [
            "Anonymous whistleblower portal",
            "Basic incident reporting",
            "Safety checklist templates",
            "Document repository",
            "Basic compliance tracking",
            "Mobile incident reports"
          ]
        },
        {
          name: "Advanced",
          monthlyPrice: 16,
          annualPrice: 13,
          color: "from-purple-500 to-purple-600",
          popular: true,
          features: [
            "Everything in Basic, plus:",
            "Encrypted whistleblower communications",
            "Advanced incident investigation",
            "Root cause analysis",
            "Safety audit trails",
            "Custom compliance workflows",
            "Risk assessment tools",
            "Automated compliance reminders"
          ]
        },
        {
          name: "Professional",
          monthlyPrice: 26,
          annualPrice: 22,
          color: "from-amber-500 to-amber-600",
          features: [
            "Everything in Advanced, plus:",
            "Multi-level case management",
            "Advanced analytics & trends",
            "Custom investigation workflows",
            "Regulatory compliance templates",
            "Safety performance metrics",
            "Integration with legal systems",
            "API access"
          ]
        },
        {
          name: "Enterprise",
          monthlyPrice: null,
          annualPrice: null,
          color: "from-slate-700 to-slate-900",
          features: [
            "Everything in Professional, plus:",
            "Unlimited case management",
            "Legal holds & e-discovery",
            "Advanced security & encryption",
            "Custom compliance frameworks",
            "Dedicated compliance officer",
            "Multi-jurisdiction support",
            "Custom integrations"
          ]
        }
      ]
    }
  };

  const currentHub = hubs[activeHub as keyof typeof hubs];
  const HubIcon = currentHub.icon;

  const getPrice = (tier: any) => {
    if (tier.monthlyPrice === null) return "Custom";
    const price = billingPeriod === "annual" ? tier.annualPrice : tier.monthlyPrice;
    return `€${price}`;
  };

  // Separate All-in-One from other hubs
  const allInOneHub = hubs.allinone;
  const otherHubs = Object.entries(hubs).filter(([key]) => key !== "allinone");
  const AllInOneIcon = allInOneHub.icon;

  return (
    <section className="py-12 px-4 bg-muted/30">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Compare plans for each module</h2>
        </div>

        {/* All-in-One Section - Featured */}
        <div className="mb-16">
          <Card className="border-2 border-primary/40 bg-gradient-to-br from-primary/5 to-purple-500/5 mb-8">
            <CardHeader>
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="bg-primary/20 rounded-lg p-3">
                  <AllInOneIcon className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-3xl">Shiftask All-in-One</CardTitle>
                <Badge className="bg-gradient-to-r from-purple-500 to-amber-500 text-white border-0">
                  Best Value
                </Badge>
              </div>
              <CardDescription className="text-center text-base">
                Complete security workforce management solution with all modules included
              </CardDescription>
              <div className="flex flex-wrap gap-3 mt-6 justify-center">
                {allInOneHub.includes.map((item, idx) => (
                  <Badge key={idx} variant="outline" className="text-sm py-1.5 px-4 border-primary/30">
                    {item}
                  </Badge>
                ))}
              </div>
            </CardHeader>
          </Card>

          {/* All-in-One Pricing Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {allInOneHub.tiers.map((tier) => (
              <Card
                key={tier.name}
                className={`relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-luxury ${
                  tier.popular ? "border-2 border-primary shadow-luxury" : ""
                }`}
              >
                {tier.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-1 text-xs font-semibold rounded-bl-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className={`absolute inset-0 bg-gradient-to-br ${tier.color} opacity-5`} />

                <CardHeader className="relative z-10">
                  <CardTitle className="text-xl font-bold">{tier.name}</CardTitle>
                  <div className="mt-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">{getPrice(tier)}</span>
                      {tier.monthlyPrice !== null && (
                        <span className="text-sm text-muted-foreground">/user/month</span>
                      )}
                    </div>
                    {billingPeriod === "annual" && tier.monthlyPrice !== null && (
                      <p className="text-xs text-muted-foreground mt-1">Billed annually</p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="relative z-10">
                  <ul className="space-y-2.5">
                    {tier.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span className={`text-sm ${feature.includes("Everything in") ? "font-semibold" : ""}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="relative z-10">
                  <Button
                    className={`w-full bg-gradient-to-r ${tier.color} text-white hover:opacity-90 shadow-enhanced`}
                    onClick={() => navigate("/auth")}
                  >
                    {tier.monthlyPrice === null ? "Contact Sales" : "Start Free Trial"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        {/* Individual Module Tabs */}
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold mb-2">Or choose individual modules</h3>
          <p className="text-muted-foreground">Select specific modules that fit your needs</p>
        </div>

        <Tabs value={activeHub} onValueChange={setActiveHub} className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto mb-8">
            {otherHubs.map(([key, hub]) => {
              const Icon = hub.icon;
              return (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="flex items-center gap-2 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{hub.name}</span>
                  <span className="sm:hidden">
                    {key === "operations" ? "Ops" : key === "communications" ? "Comms" : key === "hr" ? "HR" : "Safety"}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {otherHubs.map(([key, hub]) => (
            <TabsContent key={key} value={key} className="space-y-8">
              {/* Hub description */}
              <Card className="border-2 border-primary/20">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-primary/10 rounded-lg p-2">
                      <HubIcon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">The {hub.name} Hub includes</CardTitle>
                  </div>
                  <CardDescription className="text-base">
                    <div className="flex flex-wrap gap-3 mt-4">
                      {hub.includes.map((item, idx) => (
                        <Badge key={idx} variant="outline" className="text-sm py-1 px-3">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Pricing tiers */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {hub.tiers.map((tier, idx) => (
                  <Card
                    key={tier.name}
                    className={`relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-luxury ${
                      tier.popular ? "border-2 border-primary shadow-luxury" : ""
                    }`}
                  >
                    {tier.popular && (
                      <div className="absolute top-0 right-0">
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-1 text-xs font-semibold rounded-bl-lg">
                          Most Popular
                        </div>
                      </div>
                    )}

                    <div className={`absolute inset-0 bg-gradient-to-br ${tier.color} opacity-5`} />

                    <CardHeader className="relative z-10">
                      <CardTitle className="text-xl font-bold">{tier.name}</CardTitle>
                      <div className="mt-4">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold">{getPrice(tier)}</span>
                          {tier.monthlyPrice !== null && (
                            <span className="text-sm text-muted-foreground">/user/month</span>
                          )}
                        </div>
                        {billingPeriod === "annual" && tier.monthlyPrice !== null && (
                          <p className="text-xs text-muted-foreground mt-1">Billed annually</p>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="relative z-10">
                      <ul className="space-y-2.5">
                        {tier.features.map((feature, featureIdx) => (
                          <li key={featureIdx} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span className={`text-sm ${feature.includes("Everything in") ? "font-semibold" : ""}`}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardFooter className="relative z-10">
                      <Button
                        className={`w-full bg-gradient-to-r ${tier.color} text-white hover:opacity-90 shadow-enhanced`}
                        onClick={() => navigate("/auth")}
                      >
                        {tier.monthlyPrice === null ? "Contact Sales" : "Start Free Trial"}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
